import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { doc, getDoc, collection, query, orderBy, onSnapshot, getDocs, writeBatch } from "firebase/firestore";
import { db, auth } from "../firebase";
import useEmployeeAssignments from '../hooks/useEmployeeAssignments';
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

export default function EmployeeWagesSection({ employeeId }) {
  const [employee, setEmployee] = useState(null);
  const [roles, setRoles] = useState([]);
  const [edit, setEdit] = useState(false);
  const [activeRateRoleId, setActiveRateRoleId] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [rateInputs, setRateInputs] = useState({});
  const [bulkRate, setBulkRate] = useState("");
  const [saving, setSaving] = useState(false);

  // Load employee and roles
  useEffect(() => {
    if (!employeeId) return;
    // Resolve tenant and read tenant-scoped employee doc
    (async () => {
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
        const utSnap = await getDoc(utRef);
        if (utSnap.exists()) {
          const ut = utSnap.data();
          tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
        }
      }
      if (!tenantId) return;
      const ref = doc(db, `tenants/${tenantId}/employees`, employeeId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data();
        setEmployee(d);
      }
    })();
  }, [employeeId]);

  // Load all role options (for label consistency)
  useEffect(() => {
    let unsub = null;
    let mounted = true;
    (async () => {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for roles listener', e);
          return;
        }
      }
      if (!tenantId) return;
        try {
        const q = query(collection(db, `tenants/${tenantId}/roles`), orderBy('name'));
        unsub = onSnapshot(q, (snap) => { if (!mounted) return; setRoleOptions(snap.docs.map((d) => ({ id: d.id, name: d.data().name, archived: d.data().archived })).filter(r => !r.archived).map(r => ({ id: r.id, name: r.name }))); }, err => { console.warn('roles listener error', err); setRoleOptions([]); });
      } catch (e) {
        console.warn('Failed to attach role options listener', e);
      }
    })();

    return () => { mounted = false; try { if (typeof unsub === 'function') unsub(); } catch (e) {} };
  }, []);

  // Prefer flattened assignments from hook; fallback to legacy employee.assignments
  const { flattened, nested: flattenedNested } = useEmployeeAssignments(employeeId);
  useEffect(() => {
    // If hook provides reconstructed nested assignments, use that; otherwise fall back to employee.assignments
    const source = (flattenedNested && Object.keys(flattenedNested).length) ? flattenedNested : (employee && employee.assignments ? employee.assignments : null);
    if (!source) {
      setRoles([]);
      return;
    }
    const assignedRoles = [];
    Object.values(source).forEach(loc => {
      if (loc.departments) {
        Object.values(loc.departments).forEach(dept => {
          if (dept.roles) {
            if (Array.isArray(dept.roles)) {
              assignedRoles.push(...dept.roles);
            } else {
              Object.entries(dept.roles).forEach(([roleId, roleData]) => {
                if (roleData && roleData.assigned) assignedRoles.push(roleId);
              });
            }
          }
        });
      }
    });
    const uniqueRoles = Array.from(new Set(assignedRoles));
    setRoles(uniqueRoles);
  }, [flattenedNested, employee]);

  const handleRateInput = (role, value) => {
    // Only allow numbers, decimals, or empty string
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setRateInputs((prev) => ({ ...prev, [role]: value }));
    }
  };

  const handleApplyBulk = () => {
    if (!bulkRate || isNaN(bulkRate)) return;
    const formatted = parseFloat(bulkRate).toFixed(2);
    setRateInputs((prev) => {
      const updated = { ...prev };
      displayedRoles.forEach((role) => {
        updated[role.id] = formatted;
      });
      return updated;
    });
  };

  const handleSave = async () => {
    if (!employeeId) return;
    setSaving(true);

    // Convert all string rates to float (or 0)
    const cleanedRates = {};
    Object.entries(rateInputs).forEach(([role, val]) => {
      cleanedRates[role] = val === "" ? 0 : parseFloat(val);
    });

    // Resolve tenant and write tenant-scoped employee doc
    let tenantId = null;
    const adminRaw = localStorage.getItem('adminUser');
    if (adminRaw) {
      try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
    }
    if (!tenantId && auth && auth.currentUser) {
      const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
      const utSnap = await getDoc(utRef);
      if (utSnap.exists()) {
        const ut = utSnap.data();
        tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
      }
    }
    if (!tenantId) return;

    await syncAssignmentWages(tenantId, employeeId, cleanedRates);
    setEdit(false);
    setActiveRateRoleId(null);
    setSaving(false);

    // Sync inputs after save
    setRateInputs(
      Object.fromEntries(
        Object.entries(cleanedRates).map(([role, val]) => [role, val.toFixed(2)])
      )
    );
  };

  // Only show rates for assigned roles, display names not IDs
  const displayedRoles = roles
    .map(roleId => roleOptions.find(r => r.id === roleId))
    .filter(Boolean);

  const assignmentWagesByRole = flattened.reduce((acc, assignment) => {
    if (!assignment?.roleId) return acc;
    const wage = assignment.wage;
    if (wage !== null && wage !== undefined && !Object.prototype.hasOwnProperty.call(acc, assignment.roleId)) {
      acc[assignment.roleId] = Number(wage);
    }
    return acc;
  }, {});

  useEffect(() => {
    if (edit) return;

    const rolesForInputs = roles
      .map(roleId => roleOptions.find(r => r.id === roleId))
      .filter(Boolean);

    const assignmentWages = flattened.reduce((acc, assignment) => {
      if (!assignment?.roleId) return acc;
      const wage = assignment.wage;
      if (wage !== null && wage !== undefined && !Object.prototype.hasOwnProperty.call(acc, assignment.roleId)) {
        acc[assignment.roleId] = Number(wage);
      }
      return acc;
    }, {});

    const nextInputs = {};
    rolesForInputs.forEach((role) => {
      const value = assignmentWages[role.id] !== undefined
        ? Number(assignmentWages[role.id])
        : employee?.rates && employee.rates[role.id] !== undefined
          ? Number(employee.rates[role.id])
          : null;

      if (Number.isFinite(value)) {
        nextInputs[role.id] = value.toFixed(2);
      }
    });

    setRateInputs(nextInputs);
  }, [edit, flattened, roles, roleOptions, employee]);

  const enterRoleEdit = (roleId, currentRate) => {
    setRateInputs((prev) => ({
      ...prev,
      [roleId]:
        prev[roleId] !== undefined
          ? prev[roleId]
          : (currentRate === null ? "" : currentRate.toFixed(2)),
    }));
    setActiveRateRoleId(roleId);
    setEdit(true);
  };

  const numericRates = displayedRoles
    .map((role) => {
      const value = assignmentWagesByRole[role.id] !== undefined
        ? Number(assignmentWagesByRole[role.id])
        : employee.rates && employee.rates[role.id] !== undefined
          ? Number(employee.rates[role.id])
          : null;
      return Number.isFinite(value) ? value : null;
    })
    .filter((value) => value !== null);

  const highestRate = numericRates.length ? Math.max(...numericRates) : null;
  const lowestRate = numericRates.length ? Math.min(...numericRates) : null;
  const averageRate = numericRates.length
    ? numericRates.reduce((sum, value) => sum + value, 0) / numericRates.length
    : null;
  const formatRate = (value) => value === null ? 'Not set' : `$${Number(value).toFixed(2)}/hr`;

  if (!employee) return <Typography>Loading...</Typography>;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 1120,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
        bgcolor: 'rgba(14, 116, 144, 0.03)',
      }}
    >
      <Box sx={{ px: 2.25, py: 1.5, bgcolor: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(4px)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.08rem' }}>
              Wages & Rates
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.88rem' }}>
              Pay setup by assigned role, with bulk updates and quick rate visibility.
            </Typography>
          </Box>
          {edit ? (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving}
                sx={{ borderRadius: 2.5, fontWeight: 800, minHeight: 36, px: 1.4 }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <IconButton onClick={() => { setEdit(false); setActiveRateRoleId(null); }} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => { setEdit(true); setActiveRateRoleId(null); }} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <EditIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      <Box sx={{ px: 2.25, py: 1.25 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.1,
          }}
        >
          {[
            { label: 'Assigned Roles', value: displayedRoles.length, helper: 'Roles currently attached' },
            { label: 'Highest Rate', value: formatRate(highestRate), helper: 'Top hourly rate on file' },
            { label: 'Lowest Rate', value: formatRate(lowestRate), helper: 'Lowest hourly rate on file' },
            { label: 'Average Rate', value: formatRate(averageRate), helper: 'Average across assigned roles' },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                p: 1.2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha('#ffffff', 0.74),
              }}
            >
              <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, fontSize: '0.68rem', lineHeight: 1.1 }}>
                {item.label}
              </Typography>
              <Typography sx={{ mt: 0.32, fontWeight: 900, fontSize: '1.02rem', lineHeight: 1.25 }}>
                {item.value}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, fontSize: '0.84rem' }}>
                {item.helper}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2.25, py: 1.5 }}>
        {edit && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ md: 'center' }}
            spacing={1.25}
            sx={{
              mb: 1.5,
              p: 1.25,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: alpha('#0ea5e9', 0.18),
              bgcolor: alpha('#0ea5e9', 0.05),
            }}
          >
            <Box sx={{ minWidth: 180 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                Bulk update role rates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Apply the same hourly rate across all assigned roles.
              </Typography>
            </Box>
            <TextField
              type="number"
              label="Set all rates"
              value={bulkRate}
              onChange={e => setBulkRate(e.target.value)}
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ width: { xs: '100%', md: 150 } }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleApplyBulk}
              disabled={!bulkRate || isNaN(bulkRate)}
              sx={{ fontWeight: 800, borderRadius: 2.5, minHeight: 38, px: 1.5 }}
            >
              Apply to all roles
            </Button>
          </Stack>
        )}

        <Stack spacing={1}>
          {displayedRoles.map((role) => {
            const roleRate = assignmentWagesByRole[role.id] !== undefined
              ? Number(assignmentWagesByRole[role.id])
              : employee.rates && employee.rates[role.id] !== undefined
                ? Number(employee.rates[role.id])
                : null;

            return (
              <Box
                key={role.id}
                sx={{
                  px: 1.35,
                  py: 1.15,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha('#ffffff', 0.74),
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ md: 'center' }}
                  justifyContent="space-between"
                  spacing={1.25}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>
                        {role.name}
                      </Typography>
                      <Chip
                        label="Assigned role"
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha('#8b5cf6', 0.08),
                          color: '#7c3aed',
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                      Set the hourly wage used for this employee when scheduled in this role.
                    </Typography>
                  </Box>

                  {edit ? (
                    <TextField
                      type="number"
                      label="Hourly Rate"
                      size="small"
                      autoFocus={activeRateRoleId === role.id}
                      value={
                        rateInputs[role.id] !== undefined
                          ? rateInputs[role.id]
                          : ""
                      }
                      onChange={e =>
                        handleRateInput(role.id, e.target.value)
                      }
                      inputProps={{ min: 0, step: 0.01, inputMode: "decimal" }}
                      sx={{ width: { xs: '100%', md: 150 } }}
                    />
                  ) : (
                    <Box
                      onClick={() => enterRoleEdit(role.id, roleRate)}
                      sx={{
                        minWidth: { xs: '100%', md: 150 },
                        px: 1.25,
                        py: 1,
                        borderRadius: 2.5,
                        bgcolor: roleRate === null ? alpha('#6b7280', 0.08) : alpha('#0f766e', 0.08),
                        border: '1px solid',
                        borderColor: roleRate === null ? alpha('#6b7280', 0.16) : alpha('#0f766e', 0.18),
                        textAlign: { xs: 'left', md: 'center' },
                        cursor: 'pointer',
                        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: `0 10px 20px ${alpha(roleRate === null ? '#6b7280' : '#0f766e', 0.12)}`,
                          borderColor: roleRate === null ? alpha('#6b7280', 0.28) : alpha('#0f766e', 0.32),
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: roleRate === null ? 'text.secondary' : '#0f766e' }}>
                        {roleRate === null ? 'Not set' : `$${roleRate.toFixed(2)}/hr`}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {displayedRoles.length === 0 && (
          <Box
            sx={{
              mt: 0.5,
              p: 2,
              borderRadius: 2.5,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: alpha('#ffffff', 0.62),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No roles assigned. Assign at least one role before setting wages.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// Helper: sync assignment docs' wage field after employee rates change
async function syncAssignmentWages(tenantId, employeeId, cleanedRates) {
  if (!tenantId || !employeeId) return;
  try {
    const assignmentsCol = collection(db, `tenants/${tenantId}/employees`, employeeId, 'assignments');
    const snap = await getDocs(assignmentsCol);
    const batch = writeBatch(db);
    let hasWrites = false;

    snap.forEach(d => {
      const data = d.data();
      const roleId = data.roleId;
      let newWage = null;
      if (cleanedRates && Object.prototype.hasOwnProperty.call(cleanedRates, roleId)) {
        newWage = cleanedRates[roleId];
      }
      // fallback to role default if needed
      if (newWage === null || newWage === undefined) {
        // try to read role doc directly (best-effort)
        // Note: synchronous read here would require additional getDoc calls; keep null fallback for now
        newWage = null;
      }
      const existingWage = data.wage === undefined ? null : data.wage;
      if ((existingWage !== newWage) && !(Number.isNaN(existingWage) && Number.isNaN(newWage))) {
        const dref = doc(db, `tenants/${tenantId}/employees`, employeeId, 'assignments', d.id);
        batch.update(dref, { wage: newWage });
        hasWrites = true;
      }
    });
    if (hasWrites) await batch.commit();
  } catch (e) {
    console.warn('syncAssignmentWages error', e);
    throw e;
  }
}
